import type { BarResponse } from "@/App/Equities/shared/services/TradingGateway"
import { TimeUnit } from "@/App/Equities/shared/utils"
import { assertNever } from "@/utils"

const MS_PER_DAY = 24 * 60 * 60 * 1_000

export function getBucketSizeMs(timeUnit: TimeUnit, interval: number): number {
  switch (timeUnit) {
    case TimeUnit.Min:
      return interval * 60 * 1_000
    case TimeUnit.Hour:
      return interval * 60 * 60 * 1_000
    // interval is ignored — timeframeConfig only ever pairs Day with interval 1.
    case TimeUnit.Day:
      return MS_PER_DAY
    default:
      assertNever(timeUnit)
  }
}

/**
 * Truncate a timestamp to the start of its period bucket.
 * E.g. for 1Hour: "2026-04-10T14:37:00Z" → "2026-04-10T14:00:00Z"
 * Buckets are anchored to the UTC epoch.
 */
export function truncateToPeriod(
  timestamp: string,
  timeUnit: TimeUnit,
  interval: number,
): number {
  const bucketMs = getBucketSizeMs(timeUnit, interval)
  return Math.floor(new Date(timestamp).getTime() / bucketMs) * bucketMs
}

export function mergeLiveBar(
  bars: BarResponse[],
  liveBar: BarResponse,
  symbol: string,
  timeUnit: TimeUnit,
  interval: number,
): BarResponse[] {
  if (!liveBar.t || bars.length === 0) return bars

  const lastHistorical = bars[bars.length - 1]
  const lastBucket = truncateToPeriod(lastHistorical.t, timeUnit, interval)
  const liveBucket = truncateToPeriod(liveBar.t, timeUnit, interval)

  const liveAsBar: BarResponse = {
    symbol,
    o: liveBar.o,
    h: liveBar.h,
    l: liveBar.l,
    c: liveBar.c,
    v: liveBar.v,
    n: liveBar.n,
    vw: liveBar.vw,
    // Candles are keyed by the start of their bucket, not by the tick that
    // opened them — Highcharts spaces points by t, so a raw tick timestamp
    // renders the final block at the wrong offset and width.
    t: new Date(liveBucket).toISOString(),
  }

  if (liveBucket === lastBucket) {
    // v, n and vw are carried over untouched: the live bar restates one minute's
    // cumulative totals, so folding them into the bucket would inflate them. No
    // consumer reads these off this path, so they are left unmodelled for now.
    const updated: BarResponse = {
      ...lastHistorical,
      h:
        Number(liveBar.h) > Number(lastHistorical.h)
          ? liveAsBar.h
          : lastHistorical.h,
      l:
        Number(liveBar.l) < Number(lastHistorical.l)
          ? liveAsBar.l
          : lastHistorical.l,
      c: liveAsBar.c,
    }
    return [...bars.slice(0, -1), updated]
  }

  // New period bucket — append as a new candle
  return [...bars, liveAsBar]
}
